import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../services/adminApiService';
import './AdminComponents.css';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
  department: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  permissions: string[];
  lastLogin?: string;
  createdDate: string;
  createdBy: string;
  assignedProducts?: Product[]; // Added for product assignment
  // SEPA & Risk fields
  userType?: string; // RETAIL, BUSINESS_NORMAL, BUSINESS_HIGH_RISK
  riskCategory?: string; // NORMAL, HIGH_RISK
  reviewRequired?: boolean;
  sepaDailyLimit?: number;
  sepaMonthlyLimit?: number;
  sepaYearlyLimit?: number;
  sepaSingleTransactionLimit?: number;
  sepaLimitCurrency?: string;
}

interface Product {
  id: number;
  name: string;
  type: string;
  customerType: string;
  description?: string;
  isAvailable: boolean;
}

const AdminUserManagement: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateRetailModal, setShowCreateRetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState<'ALL' | 'ADMIN' | 'RETAIL'>('ALL');
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');

  const [newRetailUser, setNewRetailUser] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    userType: 'RETAIL',
    riskCategory: 'NORMAL',
    password: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Mobile detection state
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    role: 'VIEWER',
    department: '',
    permissions: [] as string[],
    password: ''
  });

  const permissionGroups = {
    'Analytics & Reports': [
      'VIEW_ANALYTICS',
      'GENERATE_REPORTS',
      'EXPORT_DATA'
    ],
    'Customer Management': [
      'VIEW_CUSTOMERS',
      'EDIT_CUSTOMERS',
      'SUSPEND_CUSTOMERS'
    ],
    'Account Management': [
      'VIEW_ACCOUNTS',
      'EDIT_ACCOUNTS',
      'FREEZE_ACCOUNTS',
      'ADJUST_BALANCES',
      'CLOSE_ACCOUNTS',
      'VIEW_ACCOUNT_HISTORY'
    ],
    'Transaction Management': [
      'VIEW_TRANSACTIONS',
      'APPROVE_TRANSACTIONS',
      'REJECT_TRANSACTIONS',
      'REVERSE_TRANSACTIONS',
      'CREATE_MANUAL_TRANSACTIONS'
    ],
    'Compliance & KYC': [
      'VIEW_COMPLIANCE_ALERTS',
      'VIEW_KYT',
      'MANAGE_COMPLIANCE_CASES',
      'VIEW_KYC_DOCUMENTS',
      'APPROVE_KYC',
      'REJECT_KYC',
      'ACCESS_AML_TOOLS',
      'CREATE_SAR_REPORTS',
      'MANAGE_SANCTIONS_LIST'
    ],
    'System Management': [
      'MANAGE_ADMIN_USERS',
      'ACCESS_AUDIT_LOGS',
      'ACCESS_API_LOGS',
      'CONFIGURE_SYSTEM',
      'CONFIGURE_PRODUCTS',
      'MANAGE_INTEGRATIONS',
      'MANAGE_FEES',
      'SET_INTEREST_RATES',
      'RECONCILE_ACCOUNTS',
      'MANAGE_LIQUIDITY',
      'FX_OPERATIONS',
      'TREASURY_OPERATIONS',
      'CREATE_CAMPAIGNS',
      'MANAGE_TRADING_BOTS',
      'MANAGE_TRADING_RECONCILIATION',
      'VIEW_TRADING_BOT_ANALYTICS',
      'VIEW_TRADING_STRATEGY_LOGS',
      'CONFIGURE_TRADING_STRATEGIES',
      'EXECUTE_TRADING_ORDERS',
      'VIEW_TRADING_BOT_PERFORMANCE',
      'PROVISION_KUCOIN_SUBACCOUNTS',
      'USE_SHARED_KUCOIN_ACCOUNT',
      'MANAGE_SOCIAL_MEDIA_POSTS',
      'VIEW_SOCIAL_MEDIA_ANALYTICS',
      'SCHEDULE_SOCIAL_MEDIA_CONTENT',
      'MODERATE_SOCIAL_MEDIA_COMMENTS',
      'MANAGE_SOCIAL_MEDIA_CAMPAIGNS'
    ],
    'Trading Bots Management': [
      'MANAGE_TRADING_BOTS',
      'MANAGE_TRADING_RECONCILIATION',
      'VIEW_TRADING_BOT_ANALYTICS',
      'VIEW_TRADING_STRATEGY_LOGS',
      'CONFIGURE_TRADING_STRATEGIES',
      'EXECUTE_TRADING_ORDERS',
      'VIEW_TRADING_BOT_PERFORMANCE',
      'PROVISION_KUCOIN_SUBACCOUNTS',
      'USE_SHARED_KUCOIN_ACCOUNT',
      'VIEW_CRYPTO_PORTFOLIO',
      'VIEW_STOCK_PORTFOLIO',
      'MANAGE_STOCK_PORTFOLIO'
    ],
    'Order Book Management': [
      'VIEW_ADMIN_ORDER_BOOK',
      'MANAGE_ORDER_BOOK_PROVIDERS',
      'VIEW_ORDER_BOOK_ANALYTICS'
    ],
    'Asset Token Management': [
      'VIEW_ASSET_TOKENS',
      'MANAGE_ASSET_TOKENS'
    ],
    'Stablecoin Management': [
      'VIEW_ADMIN_STABLECOIN',
      'MANAGE_STABLECOIN_CONFIG'
    ],
    'Subscription Management': [
      'VIEW_ADMIN_SUBSCRIPTIONS',
      'MANAGE_SUBSCRIPTIONS',
      'APPROVE_SUBSCRIPTION_CRYPTO_PAYMENTS',
      'APPROVE_ADMIN_SUBSCRIPTION_CRYPTO_PAYMENTS'
    ],
    'TrueLayer Top-Up Reconciliation': [
      'VIEW_TRUELAYER_TOPUP',
      'MANAGE_TRUELAYER_TOPUP'
    ],
    'Landing Page Management': [
      'VIEW_LANDING_PAGE',
      'MANAGE_LANDING_PAGE'
    ],
    'FAQ Management': [
      'VIEW_FAQ',
      'MANAGE_FAQ'
    ],
    'Creator Chat Modals': [
      'VIEW_CREATOR_CHAT',
      'MANAGE_CREATOR_CHAT'
    ],
    'Login Display Settings': [
      'MANAGE_LOGIN_DISPLAY_SETTINGS'
    ],
    'Market signals': [
      'VIEW_MARKET_SIGNALS',
      'OPEN_MARKET_SIGNAL_POSITIONS'
    ],
    'Market Intelligence': [
      'VIEW_ADMIN_MARKETS'
    ],
    'Settings Management': [
      'MANAGE_SETTINGS',
      'MANAGE_PROVIDER_CONFIG'
    ],
    'Content Management': [
      'VIEW_CONTENT',
      'MANAGE_CONTENT',
      'APPROVE_CONTENT',
      'DELETE_CONTENT',
      'MANAGE_CAMPAIGNS',
      'VIEW_CONTENT_ANALYTICS'
    ],
    'Compliance Page Sections': [
      'VIEW_ADMIN_COMPLIANCE_BASIC',
      'VIEW_ADMIN_COMPLIANCE_KYC_REVIEW',
      'VIEW_ADMIN_COMPLIANCE_KYB_REVIEW'
    ],
    'Admin Sidebar Menu Access': [
      'VIEW_ADMIN_DASHBOARD',
      'VIEW_ADMIN_CUSTOMERS',
      'VIEW_ADMIN_ACCOUNTS',
      'VIEW_ADMIN_TRANSACTIONS',
      'VIEW_ADMIN_COMPLIANCE',
      'VIEW_ADMIN_KYC',
      'VIEW_ADMIN_KYT',
      'VIEW_ADMIN_ANALYTICS',
      'VIEW_ADMIN_REPORTS',
      'VIEW_ADMIN_SETTINGS',
      'VIEW_ADMIN_USERS',
      'VIEW_ADMIN_AUDIT',
      'VIEW_IP_BLOCKLIST',
      'MANAGE_IP_BLOCKLIST',
      'VIEW_ADMIN_PRODUCTS',
      'VIEW_ADMIN_ASSET_TOKENS',
      'VIEW_ADMIN_INTEGRATIONS',
      'VIEW_ADMIN_TREASURY',
      'VIEW_ADMIN_CAMPAIGNS',
      'VIEW_ADMIN_TRADING_BOTS',
      'VIEW_ADMIN_TRADING_BOTS_TAB',
      'VIEW_ADMIN_TRADING_STRATEGIES_TAB',
      'VIEW_CRYPTO_PORTFOLIO',
      'VIEW_STOCK_PORTFOLIO',
      'MANAGE_STOCK_PORTFOLIO',
      // NOTE: KuCoin spot-coins visibility controls (section + per-tab permissions).
      'VIEW_ADMIN_TRADING_SPOT_COINS_SECTION',
      'VIEW_ADMIN_TRADING_SPOT_COINS_FUNDING_TAB',
      'VIEW_ADMIN_TRADING_SPOT_COINS_TRADING_TAB',
      'VIEW_ADMIN_TRADING_SPOT_COINS_MARGIN_TAB',
      'VIEW_ADMIN_TRADING_SPOT_COINS_FUTURES_TAB',
      'VIEW_ADMIN_ORDER_BOOK',
      'VIEW_ADMIN_MARKETS',
      'VIEW_ADMIN_SOCIAL_MEDIA',
      'VIEW_ADMIN_IGAMING',
      'VIEW_ADMIN_P2PGAME',
      'VIEW_ADMIN_CRYPTOPAYOUTS',
      'VIEW_PLATFORM_COINS',
      'MANAGE_PLATFORM_COINS',
      'MANAGE_AIRDROP_CAMPAIGNS',
      'VIEW_ADMIN_STABLECOIN',
      'VIEW_CLIENT_FEATURE_SELECTOR',
      'VIEW_ADMIN_LANDING_PAGE',
      'VIEW_CREATOR_CHAT',
      'VIEW_ADMIN_SUBSCRIPTIONS',
      'APPROVE_SUBSCRIPTION_CRYPTO_PAYMENTS',
      'APPROVE_ADMIN_SUBSCRIPTION_CRYPTO_PAYMENTS'
    ],
    'Admin Dashboard Card Access': [
      'VIEW_DASHBOARD_TOTAL_CUSTOMERS',
      'VIEW_DASHBOARD_ACTIVE_CUSTOMERS',
      'VIEW_DASHBOARD_TOTAL_ACCOUNTS',
      'VIEW_DASHBOARD_CRYPTO_PORTFOLIO',
      'VIEW_DASHBOARD_ASSET_PORTFOLIO',
      'VIEW_DASHBOARD_COMPLIANCE_ALERTS',
      'VIEW_DASHBOARD_PENDING_TRANSACTIONS',
      'VIEW_DASHBOARD_SYSTEM_HEALTH',
      'VIEW_DASHBOARD_QUICK_INSIGHTS',
      'VIEW_DASHBOARD_SYSTEM_STATUS',
      'VIEW_DASHBOARD_KYC_REVIEWS',
      'VIEW_DASHBOARD_KYB_REVIEWS',
      'VIEW_DASHBOARD_TRADING_BOTS'
    ],
    'Retail Sidebar Menu Access': [
      'VIEW_RETAIL_DASHBOARD',
      'VIEW_RETAIL_ACCOUNTS',
      'VIEW_RETAIL_TRANSACTIONS',
      'VIEW_RETAIL_TRANSFER',
      'VIEW_RETAIL_PAYMENTS',
      'VIEW_RETAIL_CARDS',
      'VIEW_RETAIL_LOANS',
      'VIEW_RETAIL_INVESTMENTS', // Keep for backward compatibility
      'VIEW_RETAIL_SAVINGS',
      'VIEW_RETAIL_CRYPTO',
      'VIEW_RETAIL_ASSETS',
      'VIEW_RETAIL_MT5_TRADING',
      'VIEW_RETAIL_TRADING_BOTS',
      'VIEW_RETAIL_CRYPTO_PORTFOLIO',
      'VIEW_RETAIL_ORDER_BOOK',
      'VIEW_RETAIL_IGAMING',
      'VIEW_RETAIL_P2PGAME',
      'VIEW_RETAIL_INSURANCE',
      'VIEW_RETAIL_PROFILE',
      'VIEW_RETAIL_CHILD_WALLETS',
      'VIEW_RETAIL_SETTINGS',
      'VIEW_RETAIL_SUPPORT',
      'VIEW_RETAIL_SOCIAL_MEDIA',
      'VIEW_RETAIL_CREATOR_CHAT',
      'VIEW_RETAIL_NOTIFICATIONS',
      'VIEW_RETAIL_DOCUMENTS',
      'VIEW_RETAIL_ALERTS'
    ],
    'Retail Dashboard Section Access': [
      'VIEW_RETAIL_DASHBOARD_BALANCE',
      'VIEW_RETAIL_DASHBOARD_QUICK_ACTIONS',
      'VIEW_RETAIL_DASHBOARD_ACCOUNTS',
      'VIEW_RETAIL_DASHBOARD_TRANSACTIONS',
      'VIEW_RETAIL_DASHBOARD_INVESTMENTS',
      'VIEW_RETAIL_DASHBOARD_MULTISIG_WALLETS',
      'VIEW_RETAIL_DASHBOARD_TRUELAYER'
    ],
    'Matching Engine / Trading': [
      'MANAGE_MATCHING_ENGINE',
      'SUBMIT_TRADING_ORDERS',
      'CANCEL_TRADING_ORDERS',
      'VIEW_TRADING_HISTORY'
    ],
    // UI controls and header visibility
    'UI Controls': [
      'VIEW_ADMIN_NOTIFICATIONS'
    ]
  };

  // Flatten all permissions for backward compatibility
  const availablePermissions = Object.values(permissionGroups).flat();

  // Permission dependency mapping for auto-enable logic
  const permissionDependencies: { [key: string]: string[] } = {
    'VIEW_ADMIN_CUSTOMERS': [
      'VIEW_CUSTOMERS',
      'EDIT_CUSTOMERS', 
      'SUSPEND_CUSTOMERS'
    ],
    'VIEW_ADMIN_ACCOUNTS': [
      'VIEW_ACCOUNTS',
      'EDIT_ACCOUNTS',
      'FREEZE_ACCOUNTS',
      'ADJUST_BALANCES',
      'CLOSE_ACCOUNTS',
      'VIEW_ACCOUNT_HISTORY'
    ],
    'VIEW_ADMIN_TRANSACTIONS': [
      'VIEW_TRANSACTIONS',
      'APPROVE_TRANSACTIONS',
      'REJECT_TRANSACTIONS',
      'REVERSE_TRANSACTIONS',
      'CREATE_MANUAL_TRANSACTIONS'
    ],
    'VIEW_ADMIN_COMPLIANCE': [
      'VIEW_COMPLIANCE_ALERTS',
      'MANAGE_COMPLIANCE_CASES',
      'VIEW_KYC_DOCUMENTS',
      'APPROVE_KYC',
      'REJECT_KYC',
      'ACCESS_AML_TOOLS',
      'CREATE_SAR_REPORTS',
      'MANAGE_SANCTIONS_LIST'
    ],
    'VIEW_ADMIN_KYT': [
      'VIEW_KYT'
    ],
    'VIEW_ADMIN_KYC': [
      'VIEW_KYT',
      'VIEW_KYC_DOCUMENTS',
      'APPROVE_KYC',
      'REJECT_KYC'
    ],
    'VIEW_ADMIN_ASSET_TOKENS': [
      'VIEW_ASSET_TOKENS'
    ],
    // Compliance page section-level access
    'VIEW_ADMIN_COMPLIANCE_BASIC': [
      'VIEW_COMPLIANCE_ALERTS'
    ],
    'VIEW_ADMIN_COMPLIANCE_KYC_REVIEW': [
      'VIEW_KYC_DOCUMENTS', 'APPROVE_KYC'
    ],
    'VIEW_ADMIN_COMPLIANCE_KYB_REVIEW': [
      'VIEW_KYC_DOCUMENTS'
    ],
    'VIEW_ADMIN_REPORTS': [
      'VIEW_ANALYTICS',
      'GENERATE_REPORTS',
      'EXPORT_DATA'
    ],
    'VIEW_ADMIN_USERS': [
      'MANAGE_ADMIN_USERS'
    ],
    'VIEW_ADMIN_AUDIT': [
      'ACCESS_AUDIT_LOGS',
      'ACCESS_API_LOGS'
    ],
    'VIEW_ADMIN_PRODUCTS': [
      'CONFIGURE_PRODUCTS'
    ],
    'VIEW_ADMIN_INTEGRATIONS': [
      'MANAGE_INTEGRATIONS',
      'MANAGE_PROVIDER_CONFIG'
    ],
    'VIEW_ADMIN_SETTINGS': [
      'MANAGE_SETTINGS',
      'CONFIGURE_SYSTEM',
      'MANAGE_FEES',
      'SET_INTEREST_RATES'
    ],
    'VIEW_ADMIN_TRADING_BOTS': [
      'MANAGE_TRADING_BOTS',
      'MANAGE_TRADING_RECONCILIATION',
      'VIEW_TRADING_BOT_ANALYTICS',
      'VIEW_TRADING_STRATEGY_LOGS',
      'CONFIGURE_TRADING_STRATEGIES',
      'EXECUTE_TRADING_ORDERS',
      'VIEW_TRADING_BOT_PERFORMANCE',
      'PROVISION_KUCOIN_SUBACCOUNTS',
      'USE_SHARED_KUCOIN_ACCOUNT',
      'VIEW_ADMIN_TRADING_SPOT_COINS_SECTION'
    ],
    // NOTE: Enabling the section permission can auto-enable all 4 account-type tabs by default.
    'VIEW_ADMIN_TRADING_SPOT_COINS_SECTION': [
      'VIEW_ADMIN_TRADING_SPOT_COINS_FUNDING_TAB',
      'VIEW_ADMIN_TRADING_SPOT_COINS_TRADING_TAB',
      'VIEW_ADMIN_TRADING_SPOT_COINS_MARGIN_TAB',
      'VIEW_ADMIN_TRADING_SPOT_COINS_FUTURES_TAB'
    ],
    'VIEW_ADMIN_ORDER_BOOK': [
      'MANAGE_ORDER_BOOK_PROVIDERS',
      'VIEW_ORDER_BOOK_ANALYTICS'
    ],
    'VIEW_ADMIN_SOCIAL_MEDIA': [
      'MANAGE_SOCIAL_MEDIA_POSTS',
      'VIEW_SOCIAL_MEDIA_ANALYTICS',
      'SCHEDULE_SOCIAL_MEDIA_CONTENT',
      'MODERATE_SOCIAL_MEDIA_COMMENTS',
      'MANAGE_SOCIAL_MEDIA_CAMPAIGNS'
    ],
    'VIEW_ADMIN_CRYPTOPAYOUTS': [
      'MANAGE_CRYPTO_PAYOUTS',
      'CREATE_ADMIN_CRYPTO_WALLETS',
      'EXECUTE_BATCH_CRYPTO_PAYOUTS',
      'VIEW_CRYPTO_PAYOUT_HISTORY',
      'VIEW_RETAIL_CRYPTO_WALLETS'
    ],
    'VIEW_PLATFORM_COINS': [
      'MANAGE_PLATFORM_COINS',
      'MANAGE_AIRDROP_CAMPAIGNS'
    ],
    'VIEW_ADMIN_STABLECOIN': [
      'MANAGE_STABLECOIN_CONFIG'
    ],
    'VIEW_TRUELAYER_TOPUP': [
      'MANAGE_TRUELAYER_TOPUP'
    ],
    'MANAGE_LANDING_PAGE': [
      'VIEW_ADMIN_LANDING_PAGE'
    ],
    'MANAGE_FAQ': [
      'VIEW_FAQ'
    ],
    'MANAGE_CREATOR_CHAT': [
      'VIEW_CREATOR_CHAT'
    ],
    'VIEW_ADMIN_SUBSCRIPTIONS': [
      'MANAGE_SUBSCRIPTIONS',
      'APPROVE_SUBSCRIPTION_CRYPTO_PAYMENTS',
      'APPROVE_ADMIN_SUBSCRIPTION_CRYPTO_PAYMENTS'
    ]
  };

  // Auto-enable handler for menu permissions
  const handleMenuPermissionChange = (menuPermission: string, isEnabled: boolean) => {
    if (!selectedUser) return;
    
    const relatedPermissions = permissionDependencies[menuPermission] || [];
    
    if (isEnabled) {
      // Enable the menu permission and all related functionality permissions
      const newPermissions = [
        ...selectedUser.permissions,
        menuPermission,
        ...relatedPermissions
      ];
      setSelectedUser({
        ...selectedUser,
        permissions: Array.from(new Set(newPermissions)) // Remove duplicates
      });
    } else {
      // Disable the menu permission and all related functionality permissions
      const newPermissions = selectedUser.permissions.filter(permission => 
        permission !== menuPermission && !relatedPermissions.includes(permission)
      );
      setSelectedUser({
        ...selectedUser,
        permissions: newPermissions
      });
    }
  };

  const getRequiredMenuPermissions = (permission: string): string[] => {
    return Object.entries(permissionDependencies)
      .filter(([, relatedPermissions]) => relatedPermissions.includes(permission))
      .map(([menuPermission]) => menuPermission);
  };

  const hasRequiredMenuAccess = (permission: string, user: AdminUser): boolean => {
    const requiredMenuPermissions = getRequiredMenuPermissions(permission);
    if (requiredMenuPermissions.length === 0) return true;
    return requiredMenuPermissions.some((menuPermission) => user.permissions.includes(menuPermission));
  };

  // Get permission description
  const getPermissionDescription = (permission: string): string => {
    const descriptions: { [key: string]: string } = {
      // Analytics & Reports
      'VIEW_ANALYTICS': 'View analytics dashboard and reports',
      'GENERATE_REPORTS': 'Generate and export system reports',
      'EXPORT_DATA': 'Export data from the system',
      
      // Customer Management
      'VIEW_CUSTOMERS': 'View customer information and profiles',
      'EDIT_CUSTOMERS': 'Edit customer information and details',
      'SUSPEND_CUSTOMERS': 'Suspend or reactivate customer accounts',
      
      // Account Management
      'VIEW_ACCOUNTS': 'View account information and balances',
      'EDIT_ACCOUNTS': 'Edit account settings and details',
      'FREEZE_ACCOUNTS': 'Freeze or unfreeze customer accounts',
      'ADJUST_BALANCES': 'Adjust account balances and transactions',
      
      // Transaction Management
      'VIEW_TRANSACTIONS': 'View transaction history and details',
      'APPROVE_TRANSACTIONS': 'Approve pending transactions',
      'REJECT_TRANSACTIONS': 'Reject pending transactions',
      
      // Compliance & KYC
      'VIEW_COMPLIANCE_ALERTS': 'View compliance alerts and notifications',
      'VIEW_KYT': 'Access KYT (Know Your Transaction) analysis tools',
      'VIEW_ADMIN_COMPLIANCE_BASIC': 'Access Basic Registration section on Compliance page',
      'VIEW_ADMIN_COMPLIANCE_KYC_REVIEW': 'Access Enhanced KYC Review section on Compliance page',
      'VIEW_ADMIN_COMPLIANCE_KYB_REVIEW': 'Access Enhanced KYB Review section on Compliance page',
      'MANAGE_COMPLIANCE_CASES': 'Manage compliance cases and investigations',
      'VIEW_KYC_DOCUMENTS': 'View KYC documents and verification status',
      'APPROVE_KYC': 'Approve or reject KYC applications',
      'MANAGE_SANCTIONS': 'Manage sanctions list and screening',
      'CREATE_SAR_REPORTS': 'Create Suspicious Activity Reports',
      'MANAGE_SANCTIONS_LIST': 'Manage sanctions and watch lists',
      
      // System Management
      'MANAGE_ADMIN_USERS': 'Create and manage admin user accounts',
      'ACCESS_AUDIT_LOGS': 'Access system audit logs',
      'ACCESS_API_LOGS': 'Access API logs and monitoring',
      'CONFIGURE_SYSTEM': 'Configure system settings and parameters',
      'CONFIGURE_PRODUCTS': 'Configure banking products and services',
      'MANAGE_INTEGRATIONS': 'Manage third-party integrations',
      'MANAGE_FEES': 'Manage fee structures and pricing',
      'SET_INTEREST_RATES': 'Set and manage interest rates',
      'RECONCILE_ACCOUNTS': 'Perform account reconciliation',
      'MANAGE_LIQUIDITY': 'Manage liquidity and cash flow',
      'FX_OPERATIONS': 'Perform foreign exchange operations',
      'TREASURY_OPERATIONS': 'Manage treasury operations',
      'CREATE_CAMPAIGNS': 'Create marketing campaigns',
      
      // Trading Bots Management
      'MANAGE_TRADING_BOTS': 'Create, edit, and delete trading bots',
      'MANAGE_TRADING_RECONCILIATION': 'Run global trading reconciliation to sync OPEN/CLOSED state with KuCoin for all strategies',
      'VIEW_TRADING_BOT_ANALYTICS': 'View trading bot performance analytics',
      'VIEW_TRADING_STRATEGY_LOGS': 'View backend logs for trading strategies',
      'CONFIGURE_TRADING_STRATEGIES': 'Configure trading strategies and parameters',
      'EXECUTE_TRADING_ORDERS': 'Execute trading orders through bots',
      'PROVISION_KUCOIN_SUBACCOUNTS': 'Create and confirm KuCoin sub-accounts under the shared master account',
      'USE_SHARED_KUCOIN_ACCOUNT': 'Assign trading strategies to the shared (system-level) KuCoin account',
      'VIEW_TRADING_BOT_PERFORMANCE': 'View trading bot performance metrics and reports',
      
      // Order Book Management
      'MANAGE_ORDER_BOOK_PROVIDERS': 'Configure and manage order book liquidity providers',
      'VIEW_ORDER_BOOK_ANALYTICS': 'View order book analytics and historical data',
      
      // Social Media Management
      'MANAGE_SOCIAL_MEDIA_POSTS': 'Create, edit, and delete social media posts',
      'VIEW_SOCIAL_MEDIA_ANALYTICS': 'View social media analytics and engagement metrics',
      'SCHEDULE_SOCIAL_MEDIA_CONTENT': 'Schedule social media content for publication',
      'MODERATE_SOCIAL_MEDIA_COMMENTS': 'Moderate social media comments and interactions',
      'MANAGE_SOCIAL_MEDIA_CAMPAIGNS': 'Manage social media campaigns and promotions',
      
      // Asset Token Management
      'VIEW_ASSET_TOKENS': 'View asset token information',
      'MANAGE_ASSET_TOKENS': 'Manage asset token operations',

      // Stablecoin Management
      'VIEW_ADMIN_STABLECOIN': 'Access the Stablecoin Management tab in the admin panel',
      'MANAGE_STABLECOIN_CONFIG': 'Create, edit, enable/disable, and delete stablecoin configurations for retail users',
      'VIEW_CLIENT_FEATURE_SELECTOR': 'Access the Client Feature Selector tool — select & configure features for new clients and launch the onboarding wizard',
      
      // Settings Management
      'MANAGE_SETTINGS': 'Manage system-wide settings and configuration',
      'MANAGE_PROVIDER_CONFIG': 'Manage external provider configurations (Sumsub, MT5, etc.)',
      
      // Content Management
      'VIEW_CONTENT': 'View content and materials',
      'MANAGE_CONTENT': 'Create and edit content',
      'APPROVE_CONTENT': 'Approve content for publication',
      'DELETE_CONTENT': 'Delete content and materials',
      'MANAGE_CAMPAIGNS': 'Manage marketing campaigns',
      'VIEW_CONTENT_ANALYTICS': 'View content performance analytics',
      
      // Admin Sidebar Menu Access
      'VIEW_ADMIN_DASHBOARD': 'Access admin dashboard page',
      'VIEW_ADMIN_CUSTOMERS': 'Access customer management section',
      'VIEW_ADMIN_ACCOUNTS': 'Access account management section',
      'VIEW_ADMIN_TRANSACTIONS': 'Access transaction management section',
      'VIEW_ADMIN_COMPLIANCE': 'Access compliance management section',
      'VIEW_ADMIN_KYC': 'Access KYC management section',
      'VIEW_ADMIN_KYT': 'Access KYT Analysis page',
      'VIEW_ADMIN_ANALYTICS': 'Access analytics and reporting section',
      'VIEW_ADMIN_REPORTS': 'Access reports section',
      'VIEW_ADMIN_SETTINGS': 'Access admin settings section',
      'VIEW_ADMIN_USERS': 'Access admin user management section',
      'VIEW_ADMIN_AUDIT': 'Access audit logs section',
      'VIEW_IP_BLOCKLIST': 'Access IP Blocklist page',
      'MANAGE_IP_BLOCKLIST': 'Add, enable, disable, or delete blocked client IP addresses',
      'VIEW_ADMIN_PRODUCTS': 'Access product management section',
      'VIEW_ADMIN_ASSET_TOKENS': 'Access Asset Tokens page',
      'VIEW_ADMIN_INTEGRATIONS': 'Access integrations section',
      'VIEW_ADMIN_TREASURY': 'Access treasury operations section',
      'VIEW_ADMIN_CAMPAIGNS': 'Access campaign management section',
      'VIEW_ADMIN_TRADING_BOTS': 'Access trading bots management section',
      'VIEW_CRYPTO_PORTFOLIO': 'Access Crypto Portfolio page — open positions, P&L, and strategy links',
      'VIEW_STOCK_PORTFOLIO': 'Access Stock Portfolio page — Trading 212 account summary, positions and order history',
      'MANAGE_STOCK_PORTFOLIO': 'Place and cancel Trading 212 stock orders from the Stock Portfolio page',
      'VIEW_ADMIN_TRADING_BOTS_TAB': 'Show Trading Bots tab inside the Trading Bots page',
      'VIEW_ADMIN_TRADING_STRATEGIES_TAB': 'Show Trading Strategies tab inside the Trading Bots page',
      'VIEW_ADMIN_TRADING_SPOT_COINS_SECTION': 'Show Trading bot spot coins section on Dashboard and Trading Bots pages',
      'VIEW_ADMIN_TRADING_SPOT_COINS_FUNDING_TAB': 'Show Funding tab in Trading bot spot coins section',
      'VIEW_ADMIN_TRADING_SPOT_COINS_TRADING_TAB': 'Show Trading (Spot) tab in Trading bot spot coins section',
      'VIEW_ADMIN_TRADING_SPOT_COINS_MARGIN_TAB': 'Show Margin tab in Trading bot spot coins section',
      'VIEW_ADMIN_TRADING_SPOT_COINS_FUTURES_TAB': 'Show Futures tab in Trading bot spot coins section',
      'VIEW_ADMIN_ORDER_BOOK': 'Access order book management section',
      'VIEW_ADMIN_SOCIAL_MEDIA': 'Access social media management section',
      'VIEW_ADMIN_IGAMING': 'Access iGaming management section',
      'VIEW_ADMIN_P2PGAME': 'Access P2P Game Betting management section',
      'VIEW_ADMIN_CRYPTOPAYOUTS': 'Access crypto payouts management section',
      'VIEW_PLATFORM_COINS': 'Access Platform Coins & Airdrops admin tab',
      'MANAGE_PLATFORM_COINS': 'Register and update platform coin metadata / listing status',
      'MANAGE_AIRDROP_CAMPAIGNS': 'Create airdrop campaigns, preview recipients, and dry-run (no live send in Phase 1)',
      
      // Admin Dashboard Card Access
      'VIEW_DASHBOARD_TOTAL_CUSTOMERS': 'View total customers card on admin dashboard',
      'VIEW_DASHBOARD_ACTIVE_CUSTOMERS': 'View active customers card on admin dashboard',
      'VIEW_DASHBOARD_TOTAL_ACCOUNTS': 'View total accounts card on admin dashboard',
      'VIEW_DASHBOARD_CRYPTO_PORTFOLIO': 'View crypto portfolio card on admin dashboard',
      'VIEW_DASHBOARD_ASSET_PORTFOLIO': 'View asset portfolio card on admin dashboard',
      'VIEW_DASHBOARD_COMPLIANCE_ALERTS': 'View compliance alerts card on admin dashboard',
      'VIEW_DASHBOARD_PENDING_TRANSACTIONS': 'View pending transactions card on admin dashboard',
      'VIEW_DASHBOARD_SYSTEM_HEALTH': 'View system health card on admin dashboard',
      'VIEW_DASHBOARD_QUICK_INSIGHTS': 'View quick insights section on admin dashboard',
      'VIEW_DASHBOARD_SYSTEM_STATUS': 'View system status section on admin dashboard',
      'VIEW_DASHBOARD_TRADING_BOTS': 'Show Trading Bots card on admin dashboard',
      'VIEW_DASHBOARD_KYC_REVIEWS': 'View KYC reviews card on admin dashboard',
      'VIEW_DASHBOARD_KYB_REVIEWS': 'View KYB reviews card on admin dashboard',
      
      // Retail Sidebar Menu Access
      'VIEW_RETAIL_DASHBOARD': 'Access main retail dashboard (overview of accounts, balances, recent activity)',
      'VIEW_RETAIL_ACCOUNTS': 'Access account information and details',
      'VIEW_RETAIL_TRANSACTIONS': 'Access transaction history and details',
      'VIEW_RETAIL_TRANSFER': 'Access money transfer functionality',
      'VIEW_RETAIL_PAYMENTS': 'Access payments and payees management',
      'VIEW_RETAIL_CARDS': 'Access debit and credit card management',
      'VIEW_RETAIL_LOANS': 'Access loan information and management',
      'VIEW_RETAIL_INVESTMENTS': 'Access all investment features (Savings, Crypto, Assets, MT5 Trading, Trading Bots) - Legacy permission',
      'VIEW_RETAIL_SAVINGS': 'Access Savings features and functionality',
      'VIEW_RETAIL_CRYPTO': 'Access Crypto trading and wallet features',
      'VIEW_RETAIL_ASSETS': 'Access Asset Tokens and investment features',
      'VIEW_RETAIL_MT5_TRADING': 'Access MT5 Trading platform and features',
      'VIEW_RETAIL_TRADING_BOTS': 'Access Trading Bots and automated trading features',
      'VIEW_RETAIL_CRYPTO_PORTFOLIO': 'Access Crypto Portfolio — view open futures positions, P&L, and linked strategies',
      'VIEW_RETAIL_IGAMING': 'Access iGaming games and betting features',
      'VIEW_RETAIL_P2PGAME': 'Access P2P Game Betting features',
      'VIEW_RETAIL_INSURANCE': 'Access insurance policy information',
      'VIEW_RETAIL_PROFILE': 'Access profile management',
      'VIEW_RETAIL_CHILD_WALLETS': 'Access child wallets management',
      'VIEW_RETAIL_SETTINGS': 'Access user settings and preferences',
      'VIEW_RETAIL_SUPPORT': 'Access support features',
      'VIEW_RETAIL_SOCIAL_MEDIA': 'Access social media features',
      'VIEW_RETAIL_CREATOR_CHAT': 'Access AI Creator Chat — browse and chat with published creator bots',
      'VIEW_RETAIL_NOTIFICATIONS': 'Access notifications and alerts',
      'VIEW_RETAIL_DOCUMENTS': 'Access document management',
      'VIEW_RETAIL_ALERTS': 'Access security alerts and notifications',
      
      // Retail Dashboard Section Access
      'VIEW_RETAIL_DASHBOARD_BALANCE': 'View account balance section on retail dashboard',
      'VIEW_RETAIL_DASHBOARD_QUICK_ACTIONS': 'View quick actions section on retail dashboard',
      'VIEW_RETAIL_DASHBOARD_ACCOUNTS': 'View accounts overview section on retail dashboard',
      'VIEW_RETAIL_DASHBOARD_TRANSACTIONS': 'View recent transactions section on retail dashboard',
      'VIEW_RETAIL_DASHBOARD_INVESTMENTS': 'View investments overview section on retail dashboard',
      'VIEW_RETAIL_DASHBOARD_MULTISIG_WALLETS': 'View multisig wallets section on retail dashboard',
      'VIEW_RETAIL_DASHBOARD_TRUELAYER': 'TrueLayer on retail dashboard: connect bank, refresh consent, linked accounts, and in-dashboard TrueLayer payments when enabled',

      // Matching Engine / Trading
      'MANAGE_MATCHING_ENGINE': 'Access Matching Engine admin page and manage matching engine settings/configuration',
      'SUBMIT_TRADING_ORDERS': 'Submit buy/sell orders to the internal matching engine',
      'CANCEL_TRADING_ORDERS': 'Cancel pending orders in the matching engine',
      'VIEW_TRADING_HISTORY': 'View executed trades and order history from the matching engine',
      
      // UI Controls
      'VIEW_ADMIN_NOTIFICATIONS': 'Show notifications bell in the admin header',

      // TrueLayer Top-Up Reconciliation
      'VIEW_TRUELAYER_TOPUP': 'View TrueLayer → KuCoin wallet top-up reconciliation page — see all top-up requests and their payment/credit statuses',
      'MANAGE_TRUELAYER_TOPUP': 'Manually credit stuck or pending TrueLayer top-up payments to a user\'s trading wallet',

      // Landing Page Management
      'VIEW_LANDING_PAGE': 'View the Market Intelligence landing page config and current publish status — read-only, no edit access',
      'MANAGE_LANDING_PAGE': 'Full landing page control — edit hero content, toggle signal sections, publish and unpublish. Also grants sidebar tab access.',
      'VIEW_ADMIN_LANDING_PAGE': 'Shows the Landing Page tab in the Admin Dashboard sidebar (auto-granted with MANAGE_LANDING_PAGE)',

      // FAQ Management
      'VIEW_FAQ': 'View the FAQ management tab in the Admin Dashboard — read-only access to FAQ items and publish status',
      'MANAGE_FAQ': 'Full FAQ control — create, edit, reorder, delete FAQ items, toggle visibility, and publish or unpublish the public /faq page. Also grants VIEW_FAQ.',
      'MANAGE_LOGIN_DISPLAY_SETTINGS': 'Manage login display behavior: default auth type (Web/API) and show/hide Login Type selector on retail/admin login pages',

      // Creator Chat Modals (Fanvue / OnlyFans)
      'VIEW_CREATOR_CHAT': 'View the Creator Chat Modals tab — list drafts and published AI chat widgets for Fanvue and OnlyFans',
      'MANAGE_CREATOR_CHAT': 'Create and edit creator AI chat modals, upload avatars, test personas before publish, and publish/unpublish widget links. Also grants VIEW_CREATOR_CHAT.',

      // Market signals (retail + admin API / unmasked landing when public page is masked)
      'VIEW_MARKET_SIGNALS': 'View full market intelligence signals (BTC regime, oracle feed, participation) even when the public landing page is masked — assign to retail users or admins who should see live data',
      'VIEW_ADMIN_MARKETS': 'Access Market Intelligence — live commodities, FX, crypto, stocks, news trigger playbooks, correlation matrix, and Trade drawer for KuCoin-mapped assets',
      'OPEN_MARKET_SIGNAL_POSITIONS': 'Open and close futures positions from the Market Signals coin drawer and Market Intelligence Trade drawer — does not grant Trading Bots, Trading Provider, or My Trading Accounts sidebar access',

      // Subscription Management
      'VIEW_ADMIN_SUBSCRIPTIONS': 'Access the Subscription Control Panel — view retail user and admin operator subscriptions, revenue dashboard, and expiry reconciliation. Also grants sidebar tab access and auto-enables MANAGE_SUBSCRIPTIONS.',
      'MANAGE_SUBSCRIPTIONS': 'Full subscription management — grant, renew, suspend, cancel, and change plans for retail users and admin operators. Also allows editing plan prices.',
      'APPROVE_SUBSCRIPTION_CRYPTO_PAYMENTS': 'Open the Sub. crypto approvals tab, list pending retail crypto subscription payments, and approve or reject payment proofs (on-chain / wallet verification). Also listed under Admin Sidebar Menu Access.',
      'APPROVE_ADMIN_SUBSCRIPTION_CRYPTO_PAYMENTS': 'Open the Admin sub. crypto approvals tab, list pending admin renewal crypto payments, and approve or reject proofs. Also listed under Admin Sidebar Menu Access.'
    };
    
    return descriptions[permission] || 'Permission description not available';
  };

  // Filter permission groups based on user type
  const getFilteredPermissionGroups = (userType: string) => {
    if (userType === 'ADMIN') {
      // For admin users, show only admin-related permissions
      return {
        'Analytics & Reports': permissionGroups['Analytics & Reports'],
        'Customer Management': permissionGroups['Customer Management'],
        'Account Management': permissionGroups['Account Management'],
        'Transaction Management': permissionGroups['Transaction Management'],
        'Compliance & KYC': permissionGroups['Compliance & KYC'],
        'System Management': permissionGroups['System Management'],
        'Asset Token Management': permissionGroups['Asset Token Management'],
        'Settings Management': permissionGroups['Settings Management'],
        'Content Management': permissionGroups['Content Management'],
        'TrueLayer Top-Up Reconciliation': permissionGroups['TrueLayer Top-Up Reconciliation'],
        'Landing Page Management': permissionGroups['Landing Page Management'],
        'FAQ Management': permissionGroups['FAQ Management'],
        'Creator Chat Modals': permissionGroups['Creator Chat Modals'],
        'Login Display Settings': permissionGroups['Login Display Settings'],
        'Matching Engine / Trading': permissionGroups['Matching Engine / Trading'],
        'Market signals': permissionGroups['Market signals'],
        'Market Intelligence': permissionGroups['Market Intelligence'],
        'Subscription Management': permissionGroups['Subscription Management'],
        'Admin Sidebar Menu Access': permissionGroups['Admin Sidebar Menu Access'],
        'Compliance Page Sections': permissionGroups['Compliance Page Sections'],
        'Admin Dashboard Card Access': permissionGroups['Admin Dashboard Card Access'],
        'UI Controls': permissionGroups['UI Controls']
      };
    } else if (userType === 'RETAIL') {
      // For retail users, show only retail-related permissions
      return {
        'Retail Sidebar Menu Access': permissionGroups['Retail Sidebar Menu Access'],
        'Retail Dashboard Section Access': permissionGroups['Retail Dashboard Section Access'],
        'Matching Engine / Trading': permissionGroups['Matching Engine / Trading'],
        'Market signals': permissionGroups['Market signals']
      };
    } else {
      // For unknown user types, show all permissions (fallback)
      return permissionGroups;
    }
  };

  const getSearchFilteredPermissionGroups = (
    userType: string,
    searchQuery: string
  ): Record<string, string[]> => {
    const filteredGroups = getFilteredPermissionGroups(userType) as Record<string, string[]>;
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) return filteredGroups;

    return Object.fromEntries(
      Object.entries(filteredGroups)
        .map(([groupName, permissions]) => {
          const matchingPermissions = permissions.filter((permission: string) => {
            const permissionText = permission.toLowerCase();
            const descriptionText = getPermissionDescription(permission).toLowerCase();
            const groupText = groupName.toLowerCase();

            return (
              permissionText.includes(normalizedQuery) ||
              descriptionText.includes(normalizedQuery) ||
              groupText.includes(normalizedQuery)
            );
          });

          return [groupName, matchingPermissions];
        })
        .filter(([, permissions]) => permissions.length > 0)
    ) as Record<string, string[]>;
  };

  const rolePermissions = {
    'SUPER_ADMIN': availablePermissions,
    'OPERATIONS_MANAGER': [
      'VIEW_ANALYTICS', 'VIEW_CUSTOMERS', 'EDIT_CUSTOMERS', 'VIEW_ACCOUNTS',
      'VIEW_TRANSACTIONS', 'VIEW_COMPLIANCE_ALERTS', 'MANAGE_SETTINGS',
      // Sidebar menu permissions
      'VIEW_ADMIN_DASHBOARD', 'VIEW_ADMIN_CUSTOMERS', 'VIEW_ADMIN_ACCOUNTS',
      'VIEW_ADMIN_TRANSACTIONS', 'VIEW_ADMIN_ANALYTICS', 'VIEW_ADMIN_SETTINGS',
      // Dashboard card permissions
      'VIEW_DASHBOARD_TOTAL_CUSTOMERS', 'VIEW_DASHBOARD_ACTIVE_CUSTOMERS',
      'VIEW_DASHBOARD_TOTAL_ACCOUNTS', 'VIEW_DASHBOARD_CRYPTO_PORTFOLIO',
      'VIEW_DASHBOARD_ASSET_PORTFOLIO', 'VIEW_DASHBOARD_COMPLIANCE_ALERTS',
      'VIEW_DASHBOARD_PENDING_TRANSACTIONS', 'VIEW_DASHBOARD_SYSTEM_HEALTH',
      'VIEW_DASHBOARD_QUICK_INSIGHTS', 'VIEW_DASHBOARD_SYSTEM_STATUS',
      'VIEW_DASHBOARD_KYC_REVIEWS', 'VIEW_DASHBOARD_KYB_REVIEWS',
      // Retail dashboard permissions
      'VIEW_RETAIL_DASHBOARD_BALANCE', 'VIEW_RETAIL_DASHBOARD_QUICK_ACTIONS',
      'VIEW_RETAIL_DASHBOARD_ACCOUNTS', 'VIEW_RETAIL_DASHBOARD_TRANSACTIONS',
      'VIEW_RETAIL_DASHBOARD_INVESTMENTS', 'VIEW_RETAIL_DASHBOARD_MULTISIG_WALLETS',
      'VIEW_RETAIL_DASHBOARD_TRUELAYER'
    ],
    'COMPLIANCE_OFFICER': [
      'VIEW_ANALYTICS', 'VIEW_CUSTOMERS', 'VIEW_ACCOUNTS', 'VIEW_TRANSACTIONS',
      'VIEW_COMPLIANCE_ALERTS', 'MANAGE_COMPLIANCE_CASES', 'ACCESS_AUDIT_LOGS',
      // Sidebar menu permissions
      'VIEW_ADMIN_DASHBOARD', 'VIEW_ADMIN_CUSTOMERS', 'VIEW_ADMIN_COMPLIANCE',
      'VIEW_ADMIN_KYC', 'VIEW_ADMIN_AUDIT', 'VIEW_ADMIN_SETTINGS',
      // Dashboard card permissions (limited for compliance)
      'VIEW_DASHBOARD_TOTAL_CUSTOMERS', 'VIEW_DASHBOARD_ACTIVE_CUSTOMERS',
      'VIEW_DASHBOARD_COMPLIANCE_ALERTS', 'VIEW_DASHBOARD_SYSTEM_HEALTH',
      'VIEW_DASHBOARD_QUICK_INSIGHTS', 'VIEW_DASHBOARD_SYSTEM_STATUS',
      'VIEW_DASHBOARD_KYC_REVIEWS', 'VIEW_DASHBOARD_KYB_REVIEWS',
      // Retail dashboard permissions (limited for compliance)
      'VIEW_RETAIL_DASHBOARD_BALANCE', 'VIEW_RETAIL_DASHBOARD_ACCOUNTS',
      'VIEW_RETAIL_DASHBOARD_TRANSACTIONS'
    ],
    'CUSTOMER_SUPPOR': [

    ] ,
    'FINANCIAL_ANALYST': [
      'VIEW_ANALYTICS', 'VIEW_TRANSACTIONS', 'VIEW_COMPLIANCE_ALERTS',
      // Sidebar menu permissions
      'VIEW_ADMIN_DASHBOARD', 'VIEW_ADMIN_TRANSACTIONS', 'VIEW_ADMIN_ANALYTICS',
      // Dashboard card permissions (financial focus)
      'VIEW_DASHBOARD_TOTAL_CUSTOMERS', 'VIEW_DASHBOARD_ACTIVE_CUSTOMERS',
      'VIEW_DASHBOARD_TOTAL_ACCOUNTS', 'VIEW_DASHBOARD_CRYPTO_PORTFOLIO',
      'VIEW_DASHBOARD_ASSET_PORTFOLIO', 'VIEW_DASHBOARD_PENDING_TRANSACTIONS',
      'VIEW_DASHBOARD_SYSTEM_HEALTH', 'VIEW_DASHBOARD_QUICK_INSIGHTS',
      'VIEW_DASHBOARD_SYSTEM_STATUS',
      // Retail dashboard permissions (financial focus)
      'VIEW_RETAIL_DASHBOARD_BALANCE', 'VIEW_RETAIL_DASHBOARD_ACCOUNTS',
      'VIEW_RETAIL_DASHBOARD_TRANSACTIONS', 'VIEW_RETAIL_DASHBOARD_INVESTMENTS',
      'VIEW_RETAIL_DASHBOARD_TRUELAYER'
    ],
    'RISK_MANAGER': [
      //Add permissions here

    ] ,
    'IT_ADMIN': [
      //Add permissions here

    ] ,
    'AUDITOR': [
      //Add permissions here

    ] ,
    'TREASURY_MANAGER': [
    //Add permissions here
    ] ,
    'VIEWER': [
      'VIEW_ANALYTICS', 'VIEW_CUSTOMERS', 'VIEW_ACCOUNTS', 'VIEW_TRANSACTIONS', 'ACCESS_AUDIT_LOGS',
      // Sidebar menu permissions
      'VIEW_ADMIN_DASHBOARD', 'VIEW_ADMIN_CUSTOMERS', 'VIEW_ADMIN_ACCOUNTS', 'VIEW_ADMIN_TRANSACTIONS',
      // Basic dashboard card permissions
      'VIEW_DASHBOARD_TOTAL_CUSTOMERS', 'VIEW_DASHBOARD_ACTIVE_CUSTOMERS',
      'VIEW_DASHBOARD_TOTAL_ACCOUNTS', 'VIEW_DASHBOARD_SYSTEM_HEALTH',
      'VIEW_DASHBOARD_QUICK_INSIGHTS', 'VIEW_DASHBOARD_SYSTEM_STATUS',
      // Basic retail dashboard permissions
      'VIEW_RETAIL_DASHBOARD_BALANCE', 'VIEW_RETAIL_DASHBOARD_ACCOUNTS',
      'VIEW_RETAIL_DASHBOARD_TRUELAYER'
    ],
    "CRYPTO_GOVERNANCE_KYT": [
      'VIEW_KYT',
      'VIEW_ADMIN_DASHBOARD',
      'VIEW_ADMIN_COMPLIANCE'
    ] ,
    "CRYPTO": [

      //Add permissions here
    ] 
  };

  // Mobile detection effect
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadUsers();
    loadAvailableProducts();
  }, [userTypeFilter]);

  useEffect(() => {
    if (showEditModal) {
      setPermissionSearchTerm('');
    }
  }, [showEditModal]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      let allUsers: AdminUser[] = [];
      
      if (userTypeFilter === 'ALL' || userTypeFilter === 'ADMIN') {
        console.log('AdminUserManagement: Loading admin users...');
        const adminResponse = await adminApiService.getAllAdminUsers();
        console.log('AdminUserManagement: Admin response received:', adminResponse);
        
        if (adminResponse.success && adminResponse.data) {
          // Mark admin users with userType
          const adminUsers = adminResponse.data.map(user => ({
            ...user,
            userType: 'ADMIN'
          }));
          allUsers = [...allUsers, ...adminUsers];
        } else {
          console.error('AdminUserManagement: Error in admin response:', adminResponse.error);
        }
      }
      
      if (userTypeFilter === 'ALL' || userTypeFilter === 'RETAIL') {
        console.log('AdminUserManagement: Loading retail users...');
        const retailResponse = await adminApiService.getAllRetailUsers();
        console.log('AdminUserManagement: Retail response received:', retailResponse);
        
        if (retailResponse.success && retailResponse.data) {
          // Mark retail users with userType
          const retailUsers = retailResponse.data.map(user => ({
            ...user,
            userType: 'RETAIL'
          }));
          allUsers = [...allUsers, ...retailUsers];
        } else {
          console.error('AdminUserManagement: Error in retail response:', retailResponse.error);
        }
      }
      
      console.log('AdminUserManagement: Setting combined users:', allUsers);
      setUsers(allUsers);
      
    } catch (err) {
      console.error('AdminUserManagement: Exception caught:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableProducts = async () => {
    try {
      const response = await adminApiService.getAvailableProducts();
      if (response.success && response.data) {
        setAvailableProducts(response.data);
      }
    } catch (err) {
      console.error('Failed to load available products:', err);
    }
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    
    // Ensure at least one character from each category
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*";
    
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest with random characters
    for (let i = 4; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleCreateUser = async () => {
    // Validate required fields
    if (!newUser.password || newUser.password.length < 8) {
      alert('Password is required and must be at least 8 characters long');
      return;
    }

    if (!newUser.department) {
      alert('Please select a department');
      return;
    }

    try {
      const userData = {
        ...newUser,
        permissions: rolePermissions[newUser.role as keyof typeof rolePermissions] || []
      };

      const response = await adminApiService.createAdminUser(userData);
      if (response.success) {
        await loadUsers();
        setShowCreateModal(false);
        setNewUser({
          username: '',
          email: '',
          firstName: '',
          lastName: '',
          role: 'VIEWER',
          department: '',
          permissions: [],
          password: ''
        });
        setShowPassword(false);
        alert('Admin user created successfully!');
      } else {
        alert(response.error || 'Failed to create admin user');
      }
    } catch (error) {
      alert('Failed to create admin user');
    }
  };

  const handleToggleUserStatus = async (userId: string, userType?: string) => {
    if (!window.confirm('Are you sure you want to toggle this user\'s status?')) {
      return;
    }

    try {
      let response;
      if (userType === 'RETAIL') {
        response = await adminApiService.toggleRetailUserStatus(userId);
      } else {
        response = await adminApiService.toggleAdminUserStatus(userId);
      }
      
      if (response.success) {
        await loadUsers();
        alert('User status updated successfully!');
      } else {
        alert(response.error || 'Failed to update user status');
      }
    } catch (error) {
      alert('Failed to update user status');
    }
  };

  const handleCreateRetailUser = async () => {
    if (!newRetailUser.username) {
      alert('Username is required');
      return;
    }
    if (!newRetailUser.password || newRetailUser.password.length < 8) {
      alert('Password is required and must be at least 8 characters');
      return;
    }
    if (!newRetailUser.firstName || !newRetailUser.lastName || !newRetailUser.email) {
      alert('Please fill in first name, last name and a valid email');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newRetailUser.email)) {
      alert('Please enter a valid email');
      return;
    }
    const resp = await adminApiService.createRetailUser({
      username: newRetailUser.username,
      firstName: newRetailUser.firstName,
      lastName: newRetailUser.lastName,
      email: newRetailUser.email,
      phone: newRetailUser.phone,
      userType: newRetailUser.userType,
      riskCategory: newRetailUser.riskCategory,
      password: newRetailUser.password
    } as any);
    if (resp.success) {
      await loadUsers();
      setShowCreateRetailModal(false);
      setNewRetailUser({ username: '', firstName: '', lastName: '', email: '', phone: '', userType: 'RETAIL', riskCategory: 'NORMAL', password: '' });
      alert('Retail user created successfully!');
    } else {
      alert(resp.error || 'Failed to create retail user');
    }
  };

  const handleAssignProducts = async () => {
    if (!selectedUser) return;

    try {
      const response = await adminApiService.assignProductsToAdminUser(
        parseInt(selectedUser.id), 
        selectedProducts
      );
      
      if (response.success) {
        alert('Products assigned successfully!');
        setShowProductModal(false);
        setSelectedProducts([]);
        await loadUsers(); // Refresh users to show updated assignments
      } else {
        alert(response.error || 'Failed to assign products');
      }
    } catch (error) {
      alert('Failed to assign products');
    }
  };

  const openProductModal = (user: AdminUser) => {
    setSelectedUser(user);
    setSelectedProducts(user.assignedProducts?.map(p => p.id) || []);
    setShowProductModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'status-badge approved';
      case 'SUSPENDED': return 'status-badge rejected';
      case 'INACTIVE': return 'status-badge inactive';
      default: return 'status-badge';
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'role-badge super-admin';      
      case 'OPERATIONS_MANAGER': return 'role-badge manager';
      case 'COMPLIANCE_OFFICER': return 'role-badge compliance';
      case 'CUSTOMER_SUPPORT': return 'role-badge customer-support';
      case 'FINANCIAL_ANALYST':  return 'role-badge analyst';
      case 'RISK_MANAGER': return 'role-badge risk-manager';
      case 'IT_ADMIN': return 'role-badge it-admin';
      case 'AUDITOR': return 'role-badge auditor';
      case 'TREASURY_MANAGER': return 'role-badge treasury-manager';
      case 'VIEWER': return 'role-badge viewer';
      default: return 'role-badge';

    }
  };



  const filteredUsers = users.filter(user => {
    const username = (user.username || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
    const term = (searchTerm || '').toLowerCase();
    const matchesSearch = username.includes(term) || email.includes(term) || fullName.includes(term);
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    const matchesUserType = userTypeFilter === 'ALL' || user.userType === userTypeFilter;
    
    return matchesSearch && matchesRole && matchesStatus && matchesUserType;
  });

  // Reset to first page when filters/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole, filterStatus, userTypeFilter]);

  const totalItems = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="admin-component-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-component-error">
        <div className="error-icon">⚠️</div>
        <h3>Error Loading Admin Users</h3>
        <p>{error}</p>
        <button className="retry-btn" onClick={loadUsers}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="admin-user-management">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h2>👥 User Management</h2>
          <p>Manage admin users and retail customers</p>
        </div>
        <div className="dashboard-actions">
          <button 
            className="create-btn"
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              background: '#10b981',
              color: 'white',
              cursor: 'pointer',
              marginRight: '12px'
            }}
          >
            ➕ Create User
          </button>
          <button 
            className="create-btn"
            onClick={() => setShowCreateRetailModal(true)}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              background: '#3b82f6',
              color: 'white',
              cursor: 'pointer',
              marginRight: '12px'
            }}
          >
            ➕ Create Retail User
          </button>
          <button className="refresh-btn" onClick={loadUsers} disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section" style={{ 
        marginBottom: '24px', 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '12px', 
        border: '1px solid #e5e7eb' 
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              Search Users
            </label>
            <input
              type="text"
              placeholder="Search by username, email, or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              User Type
            </label>
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value as 'ALL' | 'ADMIN' | 'RETAIL')}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="ALL">All Users</option>
              <option value="ADMIN">Admin Users</option>
              <option value="RETAIL">Retail Users</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              Role
            </label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Roles</option>
              <option value="RETAIL_USER">Retail User</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="OPERATIONS_MANAGER">Operations Manager</option>
              <option value="COMPLIANCE_OFFICER">Compliance Officer</option>
              <option value="CUSTOMER_SUPPORT">Customer Support</option>
              <option value="FINANCIAL_ANALYST">Financial Analyst</option>
              <option value="RISK_MANAGER">Risk Manager</option>
              <option value="IT_ADMIN">IT Admin</option>
              <option value="AUDITOR">Auditor</option>
              <option value="TREASURY_MANAGER">Treasury Manager</option>
              <option value="VIEWER">Viewer</option>
              <option value="CRYPTO_GOVERNANCE_KYT">Crypto Governance KYT</option>
              <option value="CRYPTO">Crypto</option>
              
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-container" style={{ 
        background: '#fff', 
        borderRadius: '12px', 
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        {isMobile ? (
          /* Mobile Card Layout */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px' }}>
            {paginatedUsers.map(user => (
              <div 
                key={user.id} 
                style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px', 
                  padding: 16,
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                {/* User Info */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: 8
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: 16, color: '#374151', marginBottom: 4 }}>
                        {user.firstName} {user.lastName}
                      </div>
                      <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 2 }}>
                        @{user.username}
                      </div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>
                        {user.email}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: 11,
                      fontWeight: '600',
                      backgroundColor: user.userType === 'ADMIN' ? '#dbeafe' : '#f0fdf4',
                      color: user.userType === 'ADMIN' ? '#1e40af' : '#166534',
                      whiteSpace: 'nowrap',
                      marginLeft: 8
                    }}>
                      {user.userType || 'ADMIN'}
                    </span>
                  </div>
                </div>

                {/* Details Grid */}
                <div style={{ display: 'grid', gap: 8, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280', fontSize: 13 }}>Role:</span>
                    <span className={getRoleBadgeClass(user.role)} style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: 12,
                      fontWeight: '500'
                    }}>
                      {user.role.replace('_', ' ')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280', fontSize: 13 }}>Department:</span>
                    <span style={{ fontWeight: 500, fontSize: 13 }}>{user.department}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280', fontSize: 13 }}>Status:</span>
                    <span className={getStatusBadgeClass(user.status)}>
                      {user.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280', fontSize: 13 }}>Last Login:</span>
                    <span style={{ color: '#6b7280', fontSize: 13 }}>
                      {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                    </span>
                  </div>
                  {user.assignedProducts && user.assignedProducts.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <span style={{ color: '#6b7280', fontSize: 13, display: 'block', marginBottom: 4 }}>Assigned Products:</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {user.assignedProducts.map(product => (
                          <span key={product.id} style={{
                            display: 'inline-block',
                            background: '#e0e7ff',
                            color: '#3730a3',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: 11,
                            fontWeight: '500'
                          }}>
                            {product.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 8
                }}>
                  {user.userType === 'ADMIN' ? (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            const currentUserResponse = await adminApiService.getAllAdminUsers();
                            if (currentUserResponse.success && currentUserResponse.data) {
                              const currentUser = currentUserResponse.data.find(u => u.id.toString() === user.id);
                              if (currentUser) {
                                setSelectedUser({ ...currentUser, userType: 'ADMIN' });
                              } else {
                                setSelectedUser(user);
                              }
                            } else {
                              setSelectedUser(user);
                            }
                          } catch (error) {
                            console.error('Error fetching current admin user data:', error);
                            setSelectedUser(user);
                          }
                          setShowEditModal(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          background: 'white',
                          color: '#374151',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                      >
                        ✏️ Edit User
                      </button>
                      <button
                        onClick={() => openProductModal(user)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          background: '#e0e7ff',
                          color: '#3730a3',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                      >
                        🏷️ Manage Products
                      </button>
                      <button
                        onClick={() => handleToggleUserStatus(user.id, user.userType)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          background: user.status === 'ACTIVE' ? '#fef3c7' : '#d1fae5',
                          color: user.status === 'ACTIVE' ? '#92400e' : '#065f46',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                      >
                        {user.status === 'ACTIVE' ? '🚫 Suspend User' : '✅ Activate User'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            const currentUserResponse = await adminApiService.getAllRetailUsers();
                            if (currentUserResponse.success && currentUserResponse.data) {
                              let content: any[] = currentUserResponse.data;
                              if (Array.isArray(currentUserResponse.data) && currentUserResponse.data.length > 0) {
                                const firstItem = currentUserResponse.data[0];
                                if (firstItem && typeof firstItem === 'object' && 'content' in firstItem) {
                                  content = (firstItem as any).content || currentUserResponse.data;
                                } else if (firstItem && typeof firstItem === 'object' && 'data' in firstItem) {
                                  content = (firstItem as any).data || currentUserResponse.data;
                                }
                              }
                              
                              const currentUser = content.find((u: any) => u.id.toString() === user.id);
                              if (currentUser) {
                                const mappedUser: AdminUser = {
                                  id: currentUser.id.toString(),
                                  username: currentUser.username,
                                  email: currentUser.email,
                                  firstName: currentUser.firstName,
                                  lastName: currentUser.lastName,
                                  userType: 'RETAIL',
                                  riskCategory: currentUser.riskCategory || 'NORMAL',
                                  status: currentUser.status ? 'ACTIVE' : 'INACTIVE',
                                  phone: currentUser.phone,
                                  role: 'RETAIL_USER',
                                  department: 'RETAIL',
                                  permissions: currentUser.permissions || [],
                                  lastLogin: currentUser.lastLoginAt,
                                  createdDate: currentUser.createdAt,
                                  sepaDailyLimit: currentUser.sepaDailyLimit,
                                  sepaMonthlyLimit: currentUser.sepaMonthlyLimit,
                                  sepaYearlyLimit: currentUser.sepaYearlyLimit,
                                  sepaSingleTransactionLimit: currentUser.sepaSingleTransactionLimit,
                                  sepaLimitCurrency: currentUser.sepaLimitCurrency,
                                  reviewRequired: currentUser.reviewRequired,
                                  createdBy: currentUser.createdBy || 'SYSTEM'
                                };
                                setSelectedUser(mappedUser);
                              } else {
                                setSelectedUser(user);
                              }
                            } else {
                              setSelectedUser(user);
                            }
                          } catch (error) {
                            console.error('Error fetching current user data:', error);
                            setSelectedUser(user);
                          }
                          setShowEditModal(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          background: 'white',
                          color: '#374151',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                      >
                        👁️ View User Details
                      </button>
                      <button
                        onClick={() => handleToggleUserStatus(user.id, user.userType)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          background: user.status === 'ACTIVE' ? '#fef3c7' : '#d1fae5',
                          color: user.status === 'ACTIVE' ? '#92400e' : '#065f46',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                      >
                        {user.status === 'ACTIVE' ? '🚫 Suspend User' : '✅ Activate User'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Desktop Table Layout */
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>User</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Role</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Department</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Assigned Products</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Last Login</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: '#374151' }}>
                        {user.firstName} {user.lastName}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        @{user.username}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {user.email}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: user.userType === 'ADMIN' ? '#dbeafe' : '#f0fdf4',
                      color: user.userType === 'ADMIN' ? '#1e40af' : '#166534'
                    }}>
                      {user.userType || 'ADMIN'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span className={getRoleBadgeClass(user.role)} style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {user.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={{ padding: '12px', color: '#374151' }}>{user.department}</td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {user.assignedProducts && user.assignedProducts.length > 0 ? (
                        user.assignedProducts.map(product => (
                          <span key={product.id} style={{
                            display: 'inline-block',
                            background: '#e0e7ff',
                            color: '#3730a3',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            {product.name}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>None</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span className={getStatusBadgeClass(user.status)}>
                      {user.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px', color: '#6b7280', fontSize: '14px' }}>
                    {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {user.userType === 'ADMIN' ? (
                        <>
                          <button
                            onClick={async () => {
                              // For admin users, fetch the most current data including permissions
                              try {
                                const currentUserResponse = await adminApiService.getAllAdminUsers();
                                if (currentUserResponse.success && currentUserResponse.data) {
                                  const currentUser = currentUserResponse.data.find(u => u.id.toString() === user.id);
                                  if (currentUser) {
                                    setSelectedUser({ ...currentUser, userType: 'ADMIN' });
                                  } else {
                                    setSelectedUser(user);
                                  }
                                } else {
                                  setSelectedUser(user);
                                }
                              } catch (error) {
                                console.error('Error fetching current admin user data:', error);
                                setSelectedUser(user);
                              }
                              setShowEditModal(true);
                            }}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              background: 'white',
                              color: '#374151',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => openProductModal(user)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              background: '#e0e7ff',
                              color: '#3730a3',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            🏷️ Products
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(user.id, user.userType)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              background: user.status === 'ACTIVE' ? '#fef3c7' : '#d1fae5',
                              color: user.status === 'ACTIVE' ? '#92400e' : '#065f46',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {user.status === 'ACTIVE' ? '🚫 Suspend' : '✅ Activate'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={async () => {
                              // For retail users, fetch the most current data including permissions
                              try {
                                const currentUserResponse = await adminApiService.getAllRetailUsers();
                                if (currentUserResponse.success && currentUserResponse.data) {
                                  let content: any[] = currentUserResponse.data;
                                  if (Array.isArray(currentUserResponse.data) && currentUserResponse.data.length > 0) {
                                    // Check if it's a paginated response
                                    const firstItem = currentUserResponse.data[0];
                                    if (firstItem && typeof firstItem === 'object' && 'content' in firstItem) {
                                      content = (firstItem as any).content || currentUserResponse.data;
                                    } else if (firstItem && typeof firstItem === 'object' && 'data' in firstItem) {
                                      content = (firstItem as any).data || currentUserResponse.data;
                                    }
                                  }
                                  
                                  const currentUser = content.find((u: any) => u.id.toString() === user.id);
                                  if (currentUser) {
                                    const mappedUser: AdminUser = {
                                      id: currentUser.id.toString(),
                                      username: currentUser.username,
                                      email: currentUser.email,
                                      firstName: currentUser.firstName,
                                      lastName: currentUser.lastName,
                                      userType: 'RETAIL',
                                      riskCategory: currentUser.riskCategory || 'NORMAL',
                                      status: currentUser.status ? 'ACTIVE' : 'INACTIVE',
                                      phone: currentUser.phone,
                                      role: 'RETAIL_USER',
                                      department: 'RETAIL',
                                      permissions: currentUser.permissions || [],
                                      lastLogin: currentUser.lastLoginAt,
                                      createdDate: currentUser.createdAt,
                                      sepaDailyLimit: currentUser.sepaDailyLimit,
                                      sepaMonthlyLimit: currentUser.sepaMonthlyLimit,
                                      sepaYearlyLimit: currentUser.sepaYearlyLimit,
                                      sepaSingleTransactionLimit: currentUser.sepaSingleTransactionLimit,
                                      sepaLimitCurrency: currentUser.sepaLimitCurrency,
                                      reviewRequired: currentUser.reviewRequired,
                                      createdBy: currentUser.createdBy || 'SYSTEM'
                                    };
                                    setSelectedUser(mappedUser);
                                  } else {
                                    setSelectedUser(user);
                                  }
                                } else {
                                  setSelectedUser(user);
                                }
                              } catch (error) {
                                console.error('Error fetching current user data:', error);
                                setSelectedUser(user);
                              }
                              setShowEditModal(true);
                            }}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              background: 'white',
                              color: '#374151',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            👁️ View
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(user.id, user.userType)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              background: user.status === 'ACTIVE' ? '#fef3c7' : '#d1fae5',
                              color: user.status === 'ACTIVE' ? '#92400e' : '#065f46',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {user.status === 'ACTIVE' ? '🚫 Suspend' : '✅ Activate'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* Pagination controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            Showing {totalItems === 0 ? 0 : startIndex + 1}-{endIndex} of {totalItems}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: currentPage === 1 ? '#f9fafb' : '#fff', color: '#374151', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '12px' }}
            >
              ◀ Prev
            </button>
            <span style={{ fontSize: '12px', color: '#374151' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: currentPage === totalPages ? '#f9fafb' : '#fff', color: '#374151', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '12px' }}
            >
              Next ▶
            </button>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#374151' }}>Create New User</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Username
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Password
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Enter password or generate one"
                      style={{
                        width: '100%',
                        padding: '8px 40px 8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: '#6b7280',
                        fontSize: '16px'
                      }}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewUser({ ...newUser, password: generateRandomPassword() })}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #3b82f6',
                      borderRadius: '6px',
                      background: '#3b82f6',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '12px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Generate
                  </button>
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Password must be at least 8 characters long
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Role
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="SUPER_ADMIN">Super Admin</option>
                    <option value="OPERATIONS_MANAGER">Operations Manager</option>
                    <option value="COMPLIANCE_OFFICER">Compliance Officer</option>
                    <option value="CUSTOMER_SUPPORT">Customer Support</option>
                    <option value="FINANCIAL_ANALYST">Financial Analyst</option>
                    <option value="RISK_MANAGER">Risk Manager</option>
                    <option value="IT_ADMIN">IT Admin</option>
                    <option value="AUDITOR">Auditor</option>
                    <option value="TREASURY_MANAGER">Treasury Manager</option>
                    <option value="VIEWER">Viewer</option>
                    <option value="CRYPTO_GOVERNANCE_KYT">Crypto Governance KYT</option>
                    <option value="CRYPTO">Crypto</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Department
                  </label>
                  <select
                    value={newUser.department}
                    onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Department</option>
                    <option value="OPERATIONS">Operations</option>
                    <option value="COMPLIANCE">Compliance</option>
                    <option value="RISK_MANAGEMENT">Risk Management</option>
                    <option value="CUSTOMER_SUPPORT">Customer Support</option>
                    <option value="FINANCE">Finance</option>
                    <option value="TREASURY">Treasury</option>
                    <option value="IT">IT</option>
                    <option value="IT_SECURITY">IT Security</option>
                    <option value="AUDIT">Audit</option>
                    <option value="PRODUCT_MANAGEMENT">Product Management</option>
                    <option value="GOVERNANCE_KYT">Governance KYT</option>
                    <option value="CRYPTO">Crypto</option>
    
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Permissions (automatically assigned based on role)
                </label>
                <div style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#6b7280',
                  background: '#f9fafb'
                }}>
                  {(rolePermissions[newUser.role as keyof typeof rolePermissions] || []).join(', ')}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleCreateUser}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#10b981',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Create User
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowPassword(false);
                }}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

  {/* Create Retail User Modal */}
  {showCreateRetailModal && (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ width: '640px', background: '#fff', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
          <h3 style={{ margin: 0, fontSize: '18px', color: '#111827' }}>Create Retail User</h3>
        </div>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ gridColumn: '1 / span 2' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: 6 }}>Username</label>
              <input type="text" value={newRetailUser.username} onChange={e => setNewRetailUser({ ...newRetailUser, username: e.target.value })} placeholder="e.g. john.doe" style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: 6 }}>First Name</label>
              <input type="text" value={newRetailUser.firstName} onChange={e => setNewRetailUser({ ...newRetailUser, firstName: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: 6 }}>Last Name</label>
              <input type="text" value={newRetailUser.lastName} onChange={e => setNewRetailUser({ ...newRetailUser, lastName: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: 6 }}>Email</label>
              <input type="email" value={newRetailUser.email} onChange={e => setNewRetailUser({ ...newRetailUser, email: e.target.value })} placeholder="user@example.com" style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: 6 }}>Phone</label>
              <input type="text" value={newRetailUser.phone} onChange={e => setNewRetailUser({ ...newRetailUser, phone: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <div style={{ gridColumn: '1 / span 2' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: 6 }}>Password</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" value={newRetailUser.password} onChange={e => setNewRetailUser({ ...newRetailUser, password: e.target.value })} placeholder="Minimum 8 characters" style={{ flex: 1, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }} />
                <button onClick={() => setNewRetailUser({ ...newRetailUser, password: generateRandomPassword() })} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f3f4f6', cursor: 'pointer' }}>Generate</button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: 6 }}>User Type</label>
              <select value={newRetailUser.userType} onChange={e => setNewRetailUser({ ...newRetailUser, userType: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}>
                <option value="RETAIL">RETAIL</option>
                <option value="BUSINESS_NORMAL">BUSINESS_NORMAL</option>
                <option value="BUSINESS_HIGH_RISK">BUSINESS_HIGH_RISK</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: 6 }}>Risk Category</label>
              <select value={newRetailUser.riskCategory} onChange={e => setNewRetailUser({ ...newRetailUser, riskCategory: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}>
                <option value="NORMAL">NORMAL</option>
                <option value="HIGH_RISK">HIGH_RISK</option>
              </select>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
          <button onClick={() => setShowCreateRetailModal(false)} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleCreateRetailUser} style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer' }}>Create Retail User</button>
        </div>
      </div>
    </div>
  )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#374151' }}>
              Edit User: {selectedUser.firstName} {selectedUser.lastName}
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  User Information
                </label>
                <div style={{
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#f9fafb'
                }}>
                  <div><strong>Username:</strong> {selectedUser.username}</div>
                  <div><strong>Email:</strong> {selectedUser.email}</div>
                  <div><strong>Department:</strong> {selectedUser.department}</div>
                  <div><strong>Created:</strong> {formatDate(selectedUser.createdDate)}</div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Manage Permissions
                </label>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  {selectedUser.userType === 'RETAIL' 
                    ? 'Note: All retail menu items require permissions. Users will only see menu items for which they have the corresponding permission.'
                    : 'Note: Admin users can be granted specific permissions to control access to different admin features and dashboard sections.'
                  }
                </p>
                <div style={{
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#f9fafb',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}>
                  <div style={{ marginBottom: '12px', fontSize: '12px', color: '#6b7280' }}>
                    Check/uncheck permissions to modify user access:
                  </div>
                  <input
                    type="text"
                    value={permissionSearchTerm}
                    onChange={(e) => setPermissionSearchTerm(e.target.value)}
                    placeholder="Search permissions by name or description..."
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      marginBottom: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      background: '#ffffff'
                    }}
                  />
                  {Object.entries(
                    getSearchFilteredPermissionGroups(
                      selectedUser.userType || 'ADMIN',
                      permissionSearchTerm
                    )
                  ).map(([groupName, permissions]) => (
                    <div key={groupName} style={{ marginBottom: '16px' }}>
                      <div style={{ 
                        fontSize: '12px', 
                        fontWeight: '600', 
                        color: '#374151', 
                        marginBottom: '8px',
                        paddingBottom: '4px',
                        borderBottom: '1px solid #e5e7eb'
                      }}>
                        {groupName}
                      </div>
                      {permissions.map((permission: string) => (
                        <div key={permission} style={{ marginBottom: '8px', marginLeft: '8px' }}>
                          <label style={{ display: 'flex', alignItems: 'flex-start', fontSize: '13px' }}>
                            <input
                              type="checkbox"
                              checked={selectedUser.permissions.includes(permission)}
                              disabled={!hasRequiredMenuAccess(permission, selectedUser)}
                              onChange={(e) => {
                                if (!selectedUser) return;
                                
                                // Check if this is a menu permission that should trigger auto-enable
                                if (permissionDependencies[permission]) {
                                  handleMenuPermissionChange(permission, e.target.checked);
                                } else {
                                  // Regular permission handling
                                  const newPermissions = e.target.checked
                                    ? [...selectedUser.permissions, permission]
                                    : selectedUser.permissions.filter(p => p !== permission);
                                  setSelectedUser({ ...selectedUser, permissions: newPermissions });
                                }
                              }}
                              style={{ 
                                marginRight: '8px', 
                                marginTop: '2px',
                                opacity: hasRequiredMenuAccess(permission, selectedUser) ? 1 : 0.5
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ 
                                color: selectedUser.permissions.includes(permission) ? '#374151' : '#6b7280',
                                fontWeight: selectedUser.permissions.includes(permission) ? '500' : 'normal',
                                marginBottom: '2px',
                                opacity: hasRequiredMenuAccess(permission, selectedUser) ? 1 : 0.6
                              }}>
                                {permission}
                                {permissionDependencies[permission] && (
                                  <span style={{ 
                                    fontSize: '10px', 
                                    color: '#f59e0b', 
                                    marginLeft: '8px',
                                    fontWeight: '600'
                                  }}>
                                    (Menu Access)
                                  </span>
                                )}
                                {!hasRequiredMenuAccess(permission, selectedUser) && (
                                  <span style={{ 
                                    fontSize: '10px', 
                                    color: '#ef4444', 
                                    marginLeft: '8px',
                                    fontWeight: '600'
                                  }}>
                                    (Requires Menu Access)
                                  </span>
                                )}
                              </div>
                              <div style={{ 
                                fontSize: '11px', 
                                color: '#9ca3af',
                                fontStyle: 'italic',
                                lineHeight: '1.3'
                              }}>
                                {getPermissionDescription(permission)}
                                {permissionDependencies[permission] && (
                                  <span style={{ display: 'block', color: '#f59e0b', fontWeight: '500' }}>
                                    Enabling this will auto-enable related functionality permissions
                                  </span>
                                )}
                                {!hasRequiredMenuAccess(permission, selectedUser) && (
                                  <span style={{ display: 'block', color: '#ef4444', fontWeight: '500' }}>
                                    This permission is auto-managed by the corresponding menu access permission
                                  </span>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  ))}
                  {Object.keys(
                    getSearchFilteredPermissionGroups(selectedUser.userType || 'ADMIN', permissionSearchTerm)
                  ).length === 0 && (
                    <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', padding: '8px 0' }}>
                      No permissions match your search.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Current Permissions
                </label>
                <div style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#6b7280',
                  background: '#f9fafb',
                  maxHeight: '100px',
                  overflow: 'auto'
                }}>
                  {selectedUser.permissions.join(', ')}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Status: <span className={getStatusBadgeClass(selectedUser.status)}>{selectedUser.status}</span>
                </label>
                <p style={{ fontSize: '12px', color: '#6b7280' }}>
                  Use the action buttons in the main table to change user status.
                </p>
              </div>

              {/* Basic Information Fields - Editable for retail users */}
              {selectedUser.userType === 'RETAIL' && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Basic Information
                  </label>
                  <div style={{
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: '#f9fafb',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    <div>
                      <label>First Name: <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        value={selectedUser.firstName || ''}
                        onChange={e => setSelectedUser({ ...selectedUser, firstName: e.target.value })}
                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                        required
                      />
                    </div>
                    <div>
                      <label>Last Name: <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        value={selectedUser.lastName || ''}
                        onChange={e => setSelectedUser({ ...selectedUser, lastName: e.target.value })}
                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                        required
                      />
                    </div>
                    <div>
                      <label>Email: <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="email"
                        value={selectedUser.email || ''}
                        onChange={e => setSelectedUser({ ...selectedUser, email: e.target.value })}
                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                        placeholder="user@example.com"
                        required
                      />
                    </div>
                    <div>
                      <label>Phone:</label>
                      <input
                        type="text"
                        value={selectedUser.phone || ''}
                        onChange={e => setSelectedUser({ ...selectedUser, phone: e.target.value })}
                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* In Edit User Modal, after current permissions and status, add SEPA/risk fields for editing */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  SEPA & Risk Controls
                </label>
                <div style={{
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#f9fafb',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div>
                    <label>User Type:</label>
                    <select
                      value={selectedUser.userType || ''}
                      onChange={e => setSelectedUser({ ...selectedUser, userType: e.target.value })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    >
                      <option value="">-- Select --</option>
                      <option value="RETAIL">Retail</option>
                      <option value="BUSINESS_NORMAL">Business (Normal Risk)</option>
                      <option value="BUSINESS_HIGH_RISK">Business (High Risk)</option>
                    </select>
                  </div>
                  <div>
                    <label>Risk Category:</label>
                    <select
                      value={selectedUser.riskCategory || ''}
                      onChange={e => setSelectedUser({ ...selectedUser, riskCategory: e.target.value })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    >
                      <option value="">-- Select --</option>
                      <option value="NORMAL">Normal</option>
                      <option value="HIGH_RISK">High Risk</option>
                    </select>
                  </div>
                  <div>
                    <label>Review Required:</label>
                    <input
                      type="checkbox"
                      checked={!!selectedUser.reviewRequired}
                      onChange={e => setSelectedUser({ ...selectedUser, reviewRequired: e.target.checked })}
                      style={{ marginLeft: '8px' }}
                    />
                  </div>
                  <div>
                    <label>SEPA Daily Limit:</label>
                    <input
                      type="number"
                      value={selectedUser.sepaDailyLimit ?? ''}
                      onChange={e => setSelectedUser({ ...selectedUser, sepaDailyLimit: e.target.value === '' ? undefined : Number(e.target.value) })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      min={0}
                    />
                  </div>
                  <div>
                    <label>SEPA Monthly Limit:</label>
                    <input
                      type="number"
                      value={selectedUser.sepaMonthlyLimit ?? ''}
                      onChange={e => setSelectedUser({ ...selectedUser, sepaMonthlyLimit: e.target.value === '' ? undefined : Number(e.target.value) })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      min={0}
                    />
                  </div>
                  <div>
                    <label>SEPA Yearly Limit:</label>
                    <input
                      type="number"
                      value={selectedUser.sepaYearlyLimit ?? ''}
                      onChange={e => setSelectedUser({ ...selectedUser, sepaYearlyLimit: e.target.value === '' ? undefined : Number(e.target.value) })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      min={0}
                    />
                  </div>
                  <div>
                    <label>SEPA Single Transaction Limit:</label>
                    <input
                      type="number"
                      value={selectedUser.sepaSingleTransactionLimit ?? ''}
                      onChange={e => setSelectedUser({ ...selectedUser, sepaSingleTransactionLimit: e.target.value === '' ? undefined : Number(e.target.value) })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      min={0}
                    />
                  </div>
                  <div>
                    <label>SEPA Limit Currency:</label>
                    <input
                      type="text"
                      value={selectedUser.sepaLimitCurrency ?? ''}
                      onChange={e => setSelectedUser({ ...selectedUser, sepaLimitCurrency: e.target.value })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      maxLength={3}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={async () => {
                  if (!selectedUser) return;
                  
                  console.log('Save Changes clicked for user:', selectedUser.id);
                  console.log('User type:', selectedUser.userType);
                  
                  try {
                    if (selectedUser.userType === 'RETAIL') {
                      // Validate required fields for retail users
                      if (!selectedUser.firstName || !selectedUser.lastName || !selectedUser.email) {
                        alert('Please fill in all required fields: First Name, Last Name, and Email');
                        return;
                      }

                      // Validate email format for retail users
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (!emailRegex.test(selectedUser.email)) {
                        alert('Please enter a valid email address (e.g., user@example.com)');
                        return;
                      }

                      // For retail users, update basic info and SEPA settings
                      const updateData = {
                        firstName: selectedUser.firstName,
                        lastName: selectedUser.lastName,
                        email: selectedUser.email,
                        phone: selectedUser.phone,
                        userType: selectedUser.userType,
                        riskCategory: selectedUser.riskCategory,
                        sepaDailyLimit: selectedUser.sepaDailyLimit,
                        sepaMonthlyLimit: selectedUser.sepaMonthlyLimit,
                        sepaYearlyLimit: selectedUser.sepaYearlyLimit,
                        sepaSingleTransactionLimit: selectedUser.sepaSingleTransactionLimit,
                        sepaLimitCurrency: selectedUser.sepaLimitCurrency
                      };
                      
                      console.log('Calling updateRetailUser...');
                      const resp = await adminApiService.updateRetailUser(selectedUser.id, updateData);
                      console.log('Retail user update response:', resp);
                      
                      // Also update permissions if they were changed
                      if (selectedUser.permissions && selectedUser.permissions.length > 0) {
                        console.log('Calling updateRetailUserPermissions...');
                        const permissionsResp = await adminApiService.updateRetailUserPermissions(selectedUser.id, selectedUser.permissions);
                        console.log('Retail user permissions update response:', permissionsResp);
                        
                        if (!permissionsResp.success) {
                          alert('User updated but failed to update permissions: ' + permissionsResp.error);
                          return;
                        }
                      }
                      
                      if (resp.success) {
                        alert('Retail user updated successfully!');
                        setShowEditModal(false);
                        setSelectedUser(null);
                        await loadUsers();
                      } else {
                        alert('Failed to update retail user: ' + resp.error);
                      }
                    } else {
                      // For admin users, update permissions first (main purpose of this modal)
                      console.log('User permissions to update:', selectedUser.permissions);
                      console.log('Calling updateAdminUserPermissions...');
                      const permissionsResp = await adminApiService.updateAdminUserPermissions(
                        selectedUser.id, 
                        selectedUser.permissions
                      );
                      console.log('Permissions update response:', permissionsResp);
                      
                      if (permissionsResp.success) {
                        // Only update SEPA/Risk settings if they have valid values
                        if (selectedUser.userType && selectedUser.userType !== '-- Select --' && 
                            selectedUser.riskCategory && selectedUser.riskCategory !== '-- Select --') {
                          
                          const updateData = {
                            userType: selectedUser.userType,
                            riskCategory: selectedUser.riskCategory,
                            reviewRequired: selectedUser.reviewRequired,
                            sepaDailyLimit: selectedUser.sepaDailyLimit,
                            sepaMonthlyLimit: selectedUser.sepaMonthlyLimit,
                            sepaYearlyLimit: selectedUser.sepaYearlyLimit,
                            sepaSingleTransactionLimit: selectedUser.sepaSingleTransactionLimit,
                            sepaLimitCurrency: selectedUser.sepaLimitCurrency
                          };
                          
                          const resp = await adminApiService.updateAdminUser(selectedUser.id, updateData);
                          if (!resp.success) {
                            alert('Permissions updated but failed to update user settings: ' + resp.error);
                            return;
                          }
                        }
                        
                        alert('Admin user updated successfully!');
                        setShowEditModal(false);
                        setSelectedUser(null);
                        await loadUsers();
                      } else {
                        alert('Failed to update permissions: ' + permissionsResp.error);
                      }
                    }
                  } catch (error) {
                    console.error('Error updating user:', error);
                    alert('Failed to update user: ' + error);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#3b82f6',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Save Changes
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Assignment Modal */}
      {showProductModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#374151' }}>
              Assign Products to {selectedUser.firstName} {selectedUser.lastName}
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#6b7280' }}>
              Select products that this compliance officer can access. Only SUPER_ADMIN can see all products.
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Available Products
              </label>
              <div style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                maxHeight: '300px',
                overflow: 'auto',
                padding: '12px'
              }}>
                {availableProducts.map(product => (
                  <div key={product.id} style={{ marginBottom: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProducts([...selectedProducts, product.id]);
                          } else {
                            setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <div>
                        <div style={{ fontWeight: '500', color: '#374151' }}>
                          {product.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {product.type} • {product.customerType} • {product.description}
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Currently Assigned Products ({selectedProducts.length})
              </label>
              <div style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#6b7280',
                background: '#f9fafb',
                minHeight: '40px'
              }}>
                {selectedProducts.length > 0 ? (
                  selectedProducts.map(productId => {
                    const product = availableProducts.find(p => p.id === productId);
                    return product ? (
                      <span key={productId} style={{
                        display: 'inline-block',
                        background: '#e0e7ff',
                        color: '#3730a3',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        margin: '2px',
                        fontSize: '11px'
                      }}>
                        {product.name}
                      </span>
                    ) : null;
                  })
                ) : (
                  'No products assigned'
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleAssignProducts}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#10b981',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Assign Products
              </button>
              <button
                onClick={() => {
                  setShowProductModal(false);
                  setSelectedProducts([]);
                }}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS for role badges */}
      <style>{`
        .role-badge.super-admin { background: #ef4444; color: white; }
        .role-badge.manager { background: #3b82f6; color: white; }
        .role-badge.compliance { background: #f59e0b; color: white; }
        .role-badge.analyst { background: #10b981; color: white; }
        .role-badge.viewer { background: #6b7280; color: white; }
      `}</style>
    </div>
  );
};

export default AdminUserManagement; 